<script lang="ts">
  interface Props {
    value: string;
    id: string;
    label?: string;
    placeholder?: string;
    required?: boolean;
    autocomplete?: AutoFill;
    minlength?: number;
    showMeter?: boolean;
    ariaLabel?: string;
    class?: string;
  }

  let {
    value = $bindable(''),
    id,
    label,
    placeholder,
    required = false,
    autocomplete = 'current-password',
    minlength,
    showMeter = false,
    ariaLabel,
    class: className = '',
  }: Props = $props();

  let visible = $state(false);

  const strength = $derived.by(() => {
    if (!value) return { score: 0, label: '' };
    const len = value.length;
    const base = len >= 12 ? 4 : len >= 10 ? 3 : len >= 8 ? 2 : 1;
    const classes =
      (/[a-z]/.test(value) ? 1 : 0) +
      (/[A-Z]/.test(value) ? 1 : 0) +
      (/\d/.test(value) ? 1 : 0) +
      (/[^a-zA-Z0-9]/.test(value) ? 1 : 0);
    const bonus = classes >= 3 ? 1 : 0;
    const score = Math.min(4, base + bonus);
    const labels = ['', 'Weak', 'Fair', 'Good', 'Strong'];
    return { score, label: labels[score] };
  });
</script>

<div class="pw-field {className}">
  {#if label}
    <label for={id} class="field-label">{label}</label>
  {/if}
  <div class="pw-wrap">
    {#if visible}
      <input
        {id}
        class="input pw-input"
        type="text"
        bind:value
        {required}
        autocomplete={autocomplete}
        {placeholder}
        {minlength}
        aria-label={ariaLabel ?? label ?? placeholder}
      />
    {:else}
      <input
        {id}
        class="input pw-input"
        type="password"
        bind:value
        {required}
        autocomplete={autocomplete}
        {placeholder}
        {minlength}
        aria-label={ariaLabel ?? label ?? placeholder}
      />
    {/if}
    <button
      type="button"
      class="pw-toggle"
      aria-label={visible ? 'Hide password' : 'Show password'}
      aria-pressed={visible}
      data-testid="pw-toggle"
      onclick={() => (visible = !visible)}
    >
      {#if visible}
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
          <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-10-7-10-7a18.45 18.45 0 0 1 5.06-5.94"/>
          <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 10 7 10 7a18.5 18.5 0 0 1-2.16 3.19"/>
          <path d="M14.12 14.12a3 3 0 1 1-4.24-4.24"/>
          <line x1="1" y1="1" x2="23" y2="23"/>
        </svg>
      {:else}
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
          <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7z"/>
          <circle cx="12" cy="12" r="3"/>
        </svg>
      {/if}
    </button>
  </div>
  {#if showMeter && value.length > 0}
    <div class="pw-meter" data-testid="pw-meter" data-score={strength.score}>
      <div class="pw-meter-bar"><div class="pw-meter-fill"></div></div>
      <span class="pw-meter-label">{strength.label}</span>
    </div>
  {/if}
</div>

<style>
  .pw-field {
    display: block;
  }
  .field-label {
    display: block;
    font-size: 0.875rem;
    color: var(--text-muted);
    margin-bottom: 0.25rem;
  }
  .pw-wrap {
    position: relative;
  }
  .pw-input {
    width: 100%;
    padding-right: 2.75rem;
  }
  .pw-toggle {
    position: absolute;
    right: 0.4rem;
    top: 50%;
    transform: translateY(-50%);
    background: transparent;
    border: none;
    cursor: pointer;
    color: var(--text-muted);
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    border-radius: 4px;
    transition: color 0.15s;
  }
  .pw-toggle:hover,
  .pw-toggle:focus-visible {
    color: var(--text);
    outline: none;
  }
  .pw-toggle:focus-visible {
    box-shadow: 0 0 0 2px var(--cyan);
  }

  .pw-meter {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-top: 0.4rem;
  }
  .pw-meter-bar {
    flex: 1;
    height: 4px;
    background: var(--border);
    border-radius: 2px;
    overflow: hidden;
  }
  .pw-meter-fill {
    height: 100%;
    width: 0;
    transition: width 0.2s ease, background-color 0.2s ease;
  }
  .pw-meter-label {
    font-size: 0.75rem;
    min-width: 3rem;
    text-align: right;
    font-weight: 500;
  }
  .pw-meter[data-score='1'] .pw-meter-fill { width: 25%; background: var(--pink); }
  .pw-meter[data-score='1'] .pw-meter-label { color: var(--pink); }
  .pw-meter[data-score='2'] .pw-meter-fill { width: 50%; background: #f59e0b; }
  .pw-meter[data-score='2'] .pw-meter-label { color: #f59e0b; }
  .pw-meter[data-score='3'] .pw-meter-fill { width: 75%; background: #eab308; }
  .pw-meter[data-score='3'] .pw-meter-label { color: #eab308; }
  .pw-meter[data-score='4'] .pw-meter-fill { width: 100%; background: var(--green); }
  .pw-meter[data-score='4'] .pw-meter-label { color: var(--green); }
</style>
